/**
 * The orcaloops.ai/connect OAuth approve route (OL-362) — eagerly registers the
 * file route and re-applies its OWN guard; the page component and its
 * atom/RPC imports are deferred to `connect.lazy.tsx` so they load only with
 * this route, never in the always-loaded `main` chunk.
 *
 * `/oauth/authorize` sends the browser to `orcaloops.ai/connect?<OAuth query>`,
 * which the `/orca` wildcard domain prefix rewrites onto `/orca/connect`. The
 * `/orca` layout guard skips `requireAdmin` because `/orca/connect` is in
 * `ORCA_PUBLIC_ROOTS`, and this route re-applies `requireAuth` in its place:
 * the person connecting Claude Code is almost never an `admin`.
 *
 * The page renders only on orca hosts (orcaloops.ai, orca.test); anywhere else
 * it is a 404, checked before the session is read.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";

import { consentSearchFrom } from "~/lib/oauth-consent-search.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 * `context.hostname` is set by the root `beforeLoad`.
 *
 * `requireAuth`, never `requireAdmin`.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const connectBeforeLoad = async (
  { context }: { readonly context: { readonly hostname?: { readonly isOrca?: boolean } | undefined } },
) => {
  if (context.hostname?.isOrca !== true) {
    throw notFound();
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  return { auth };
};

/**
 * The document head: a title and `noindex` — a sign-in prompt is never a search
 * result — and the v5 frame's sheet, so the page paints styled.
 *
 * @since 1.0.0
 */
export const connectHead = () => ({
  meta: [{ title: "Approve sign-in — Orcaloops" }, { name: "robots", content: "noindex" }],
  links: orcaloopsStylesheetLinks,
});

export const Route = createFileRoute("/orca/connect")({
  validateSearch: consentSearchFrom,
  beforeLoad: connectBeforeLoad,
  head: connectHead,
});
